import { Dtos } from '#models';
import { HttpClient } from '@angular/common/http';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '#environments';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TaskPlanService {
  private http = inject(HttpClient);

  // ---------------TASK PLAN------------------------------------

  public getTaskPlans(buid: number): Observable<Dtos.TaskPlan[]> {
    return this.http.get<Dtos.TaskPlan[]>(`${environment.apiUrl}/task-plan/getTaskPlans/${buid}`);
  }

  public getTaskPlanById(id: number): Observable<Dtos.TaskPlan> {
    return this.http.get<Dtos.TaskPlan>(`${environment.apiUrl}/task-plan/getTaskPlanById/${id}`);
  }

  public createTaskPlan(taskPlan: Dtos.TaskPlan): Observable<Dtos.TaskPlan> {
    return this.http.post<Dtos.TaskPlan>(`${environment.apiUrl}/task-plan/createTaskPlan`, taskPlan);
  }

  public updateTaskPlan(taskPlan: Dtos.TaskPlan): Observable<Dtos.TaskPlan> {
    return this.http.put<Dtos.TaskPlan>(`${environment.apiUrl}/task-plan/updateTaskPlan`, taskPlan);
  }

  public deleteTaskPlan(id: number) {
    return this.http.put(`${environment.apiUrl}/task-plan/deleteTaskPlan/${id}`, null);
  }

  public resetTaskPlan(id: number): Observable<Dtos.TaskPlan> {
    return this.http.put<Dtos.TaskPlan>(`${environment.apiUrl}/task-plan/resetTaskPlan/${id}`, null);
  }

  // ---------------TASK PLAN SECTION------------------------------------

  public getTaskPlanSectionById(id: number): Observable<Dtos.TaskPlanSection[]> {
    return this.http.get<Dtos.TaskPlanSection[]>(`${environment.apiUrl}/task-plan/getTaskPlanSectionById/${id}`);
  }

  public getTaskPlanSectionByTaskPlan(taskPlanId: number): Observable<Dtos.TaskPlanSection[]> {
    return this.http.get<Dtos.TaskPlanSection[]>(
      `${environment.apiUrl}/task-plan/getTaskPlanSectionByTaskPlan/${taskPlanId}`
    );
  }

  public createTaskPlanSection(taskPlanSection: Dtos.TaskPlanSection): Observable<Dtos.TaskPlanSection> {
    return this.http.post<Dtos.TaskPlanSection>(
      `${environment.apiUrl}/task-plan/createTaskPlanSection`,
      taskPlanSection
    );
  }

  public updateTaskPlanSection(taskPlanSection: Dtos.TaskPlanSection): Observable<Dtos.TaskPlanSection> {
    return this.http.put<Dtos.TaskPlanSection>(
      `${environment.apiUrl}/task-plan/updateTaskPlanSection`,
      taskPlanSection
    );
  }

  public deleteTaskPlanSection(id: number): Observable<Dtos.TaskPlanSection> {
    return this.http.put<Dtos.TaskPlanSection>(`${environment.apiUrl}/task-plan/deleteTaskPlanSection/${id}`, null);
  }

  public moveUpTaskPlanSection(id: number): Observable<Dtos.TaskPlanSection> {
    return this.http.put<Dtos.TaskPlanSection>(`${environment.apiUrl}/task-plan/moveUpTaskPlanSection/${id}`, null);
  }

  public moveDownTaskPlanSection(id: number): Observable<Dtos.TaskPlanSection> {
    return this.http.put<Dtos.TaskPlanSection>(`${environment.apiUrl}/task-plan/moveDownTaskPlanSection/${id}`, null);
  }

  // ---------------TASK PLAN SUB SECTION------------------------------------

  public getTaskPlanSubSections(id: number): Observable<Dtos.TaskPlanSubSection[]> {
    return this.http.get<Dtos.TaskPlanSubSection[]>(`${environment.apiUrl}/task-plan/getTaskPlanSubSections/${id}`);
  }

  public getTaskPlanSubSectionById(id: number): Observable<Dtos.TaskPlanSubSection> {
    return this.http.get<Dtos.TaskPlanSubSection>(`${environment.apiUrl}/task-plan/getTaskPlanSubSectionById/${id}`);
  }

  public createTaskPlanSubSection(taskPlanSubSection: Dtos.TaskPlanSubSection): Observable<Dtos.TaskPlanSubSection> {
    return this.http.post<Dtos.TaskPlanSubSection>(
      `${environment.apiUrl}/task-plan/createTaskPlanSubSection`,
      taskPlanSubSection
    );
  }

  public updateTaskPlanSubSection(taskPlanSubSection: Dtos.TaskPlanSubSection): Observable<Dtos.TaskPlanSubSection> {
    return this.http.put<Dtos.TaskPlanSubSection>(
      `${environment.apiUrl}/task-plan/updateTaskPlanSubSection`,
      taskPlanSubSection
    );
  }

  public deleteTaskPlanSubSection(id: number): Observable<Dtos.TaskPlanSubSection> {
    return this.http.put<Dtos.TaskPlanSubSection>(
      `${environment.apiUrl}/task-plan/deleteTaskPlanSubSection/${id}`,
      null
    );
  }

  public moveUpTaskPlanSubSection(id: number): Observable<Dtos.TaskPlanSubSection> {
    return this.http.put<Dtos.TaskPlanSubSection>(
      `${environment.apiUrl}/task-plan/moveUpTaskPlanSubSection/${id}`,
      null
    );
  }

  public moveDownTaskPlanSubSection(id: number): Observable<Dtos.TaskPlanSubSection> {
    return this.http.put<Dtos.TaskPlanSubSection>(
      `${environment.apiUrl}/task-plan/moveDownTaskPlanSubSection/${id}`,
      null
    );
  }

  // ---------------TASK PLAN ITEM ------------------------------------

  public getTaskPlanItemById(id: number): Observable<Dtos.TaskPlanItem> {
    return this.http.get<Dtos.TaskPlanItem>(`${environment.apiUrl}/task-plan/getTaskPlanItem/${id}`);
  }

  public createTaskPlanItem(taskPlanItem: Dtos.TaskPlanItem): Observable<Dtos.ResultModel> {
    return this.http.post<Dtos.ResultModel>(`${environment.apiUrl}/task-plan/createTaskPlanItem`, taskPlanItem);
  }

  public updateTaskPlanItem(taskPlanItem: Dtos.TaskPlanItem): Observable<Dtos.ResultModel> {
    return this.http.put<Dtos.ResultModel>(`${environment.apiUrl}/task-plan/updateTaskPlanItem`, taskPlanItem);
  }

  public deleteTaskPlanItem(id: number): Observable<Dtos.TaskPlanItem> {
    return this.http.put<Dtos.TaskPlanItem>(`${environment.apiUrl}/task-plan/deleteTaskPlanItem/${id}`, null);
  }

  public moveUpTaskPlanItem(id: number): Observable<Dtos.TaskPlanItem> {
    return this.http.put<Dtos.TaskPlanItem>(`${environment.apiUrl}/task-plan/moveUpTaskPlanItem/${id}`, null);
  }

  public moveDownTaskPlanItem(id: number): Observable<Dtos.TaskPlanItem> {
    return this.http.put<Dtos.TaskPlanItem>(`${environment.apiUrl}/task-plan/moveDownTaskPlanItem/${id}`, null);
  }

  // ------ GENERAL FUNCTION

  public isInvalidString(value: string): boolean {
    return value == undefined || value == null || value.length == 0;
  }

  public isInvalidInt(value: number): boolean {
    return value == undefined || value == null || value == 0;
  }

  public getNextItemCode(checklistId: number): Observable<string> {
    return this.http
      .get<Dtos.ResultModel>(`${environment.apiUrl}/task-plan/getNextTaskPlanItemCode/${checklistId}`)
      .pipe(switchMap(res => (res.Success ? of(res.Message) : throwError(() => new Error(res.Message)))));
  }
}
